import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type React from 'react';
import { useRef, useState } from 'react';
import { Form } from '../Form';
import type { InputSizes, InputState } from '../InputText';
import { InputTextField } from '../InputTextField';

export interface ContentEditableInputProps {
  children: React.ReactNode;
  onChange?: (newValue: string) => void;
  multiline?: boolean;
  state?: InputState;
  inputSize?: InputSizes;
  bordered?: boolean;
  disabled?: boolean;
}

export const ContentEditableInput = ({
  children,
  onChange,
  multiline,
  state,
  inputSize,
  bordered,
  disabled,
}: ContentEditableInputProps) => {
  const [isEditing, setIsEditing] = useBoolean();
  const [inputLabel, setInputLabel] = useState<string>('');
  const childRef = useRef<HTMLDivElement>(null);

  const onChildClick = () => {
    const childrenRef = childRef?.current;
    setInputLabel(childrenRef?.textContent?.trim() || '');
    setIsEditing.setTrue();
  };

  const onBlur = (event) => {
    const newValue = event.target.value.trim() || inputLabel;

    if (newValue !== inputLabel) {
      if (onChange) {
        onChange(newValue);
      }
    }
    setIsEditing.setFalse();
  };

  const onEscape = () => {
    if (onChange) {
      onChange(inputLabel);
    }
    setIsEditing.setFalse();
  };

  return isEditing ? (
    <Form
      onSubmit={onBlur}
      initialValues={{ editableInput: inputLabel }}
      validationSchema={undefined}
      validateOnBlur
    >
      <InputTextField
        name="editableInput"
        multiline={multiline}
        autoFocus
        onBlur={onBlur}
        value={inputLabel}
        onKeyDown={(event) => {
          if (event.code === 'Enter' && !multiline) {
            onBlur(event);
          }
          if (event.code === 'Escape') {
            onEscape();
          }
        }}
        noAddedHeight
        state={state}
        inputSize={inputSize}
        label=""
      />
    </Form>
  ) : (
    <div
      {...(!disabled && { onClick: onChildClick })}
      ref={childRef}
      className={classNames({
        'hover:cursor-pointer': !disabled,
        'border border-transparent rounded-md hover:border-ds-natural-150':
          bordered && !disabled,
      })}
    >
      {children}
    </div>
  );
};
